import { useEffect, useRef } from "react";
import { API_BASE, apiFetch } from "../api";

// Keeps a live connection to the server while the screen is open. Whenever
// something changes, `onEvent` is called straight away with the event:
//   { kind: "tasks", taskId, title?, message? }   something changed (title/message: an alert for me)
//   { kind: "resync" }                             connection (re)started or app came back to the front,
//                                                  so anything might have been missed: reload
// The connection is a normal fetch stream (not EventSource) so it can send the login token.
export const useRealtime = (onEvent) => {
  const handler = useRef(onEvent);
  useEffect(() => {
    handler.current = onEvent;
  });

  useEffect(() => {
    let stopped = false;
    let controller = null;
    let timer = null;
    let delay = 1000;

    const handle = (event) => {
      try {
        handler.current(event);
      } catch {
        // a screen's handler must never kill the connection
      }
    };

    const connect = async () => {
      controller = new AbortController();
      try {
        const res = await apiFetch(`${API_BASE}/events`, { signal: controller.signal, headers: { Accept: "text/event-stream" } });
        if (!res.ok || !res.body) throw new Error("no stream");
        delay = 1000;
        handle({ kind: "resync" });

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let end;
          while ((end = buffer.indexOf("\n\n")) >= 0) {
            const block = buffer.slice(0, end);
            buffer = buffer.slice(end + 2);
            const type = /^event: (.*)$/m.exec(block);
            const data = /^data: (.*)$/m.exec(block);
            if (type && type[1] === "change" && data) handle(JSON.parse(data[1]));
          }
        }
      } catch {
        // dropped connection, offline, or aborted: fall through to reconnect
      }
      if (!stopped) {
        timer = setTimeout(connect, delay);
        delay = Math.min(delay * 2, 30000);
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") handle({ kind: "resync" });
    };

    connect();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      clearTimeout(timer);
      if (controller) controller.abort();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);
};
